import { css } from "@emotion/css";
import { Key } from "react";
import { useTranslation } from "react-i18next";
import { Item } from "react-stately";

import { useExternalScript } from "@certn/cascadia-react";

import { SearchableSelect } from "./SearchableSelect";
import { GoogleAddressSelectProps, onSelectionChangeType } from "./types";
import { useAddressPredictions } from "./useAddressPredictions";

declare global {
  interface Window {
    initMap: any;
  }
}

window.initMap = function () {
  window.initMap = null;
};

// To be used inside Formik Field, pass in onSelectionChange in order to
// retrieve the address components for auto filling other fields
// ******
// <Field
//  as={GoogleAddressSelectField}
//  onSelectionChange={(value: any[]) => console.log("onSelectionChange: ", value)
// />
// ******

/**
 * @deprecated use AddressTextField instead.
 */
export function GoogleAddressSelect({
  onSelectionChange,
  onChange,
  initialValue,
  value,
  region,
  locale,
  apiKey,
  onBlur,
  ...rest
}: GoogleAddressSelectProps) {
  const { t } = useTranslation();

  // we want to use the Google API keys in .env when not on localhost
  const localhostAPIKey = "AIzaSyC7d2dEkj0JyVxA3W4ffv2qQ9a4OrG5Xzo";
  const isLocalhost = window.location.hostname === "localhost";
  const key = isLocalhost ? localhostAPIKey : apiKey;

  // add Google API script to html body
  const state = useExternalScript(
    `https://maps.googleapis.com/maps/api/js?key=${key}&callback=initMap&libraries=places`
  );

  const { getPlacePredictions, getDetails, predictions } =
    useAddressPredictions({
      apiKey: key,
      region,
      locale,
    });

  const handleInput = async (value: string) => {
    getPlacePredictions(value);

    if (onChange) onChange(value); // Formik helper
  };

  const handleSelect = async (placeId: string) => {
    if (!placeId) return null;
    try {
      const details = await getDetails(placeId);
      onSelectionChange(details as onSelectionChangeType);
    } catch (e) {
      console.error("Error getting place information", e);
    }
  };

  return (
    <SearchableSelect
      allowsCustomValue
      hasHighlightOptions
      isGoogleAutocomplete
      onChange={handleInput}
      onSelectionChange={handleSelect as (key: Key) => void}
      items={predictions?.length > 0 ? predictions : undefined}
      defaultInputValue={initialValue}
      trailingItem={poweredByGoogle}
      closeText={t<string>("Suggestions")}
      isDisabled={state === "loading"}
      testID="google-address-select"
      onBlur={onBlur}
      {...rest}
    >
      {predictions?.map(({ description, place_id }) => {
        return <Item key={place_id}>{description}</Item>;
      })}
    </SearchableSelect>
  );
}

const poweredByGoogle = (
  <div
    className={css({
      width: "35px",
      display: "flex",
      marginLeft: "var(--space-1)",
      marginTop: "var(--space-2)",
      marginBottom: "var(--space-1)",
    })}
  >
    <img
      className="powered-by-google"
      src="https://storage.googleapis.com/geo-devrel-public-buckets/powered_by_google_on_white.png"
      alt="Powered by Google"
    />
  </div>
);
